import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { ITALIAN_PROVINCES } from '../constants/cities';

interface PublicRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PublicRegistrationModal: React.FC<PublicRegistrationModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    birthdate: '',
    city: 'ROMA'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '', gender: '', birthdate: '', city: 'ROMA' });
    setSuccess(false);
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-register`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Errore durante la registrazione');
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Errore durante la registrazione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#D91C5C] to-[#8B1538] p-5 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">
            Registrati
          </h2>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Registrazione completata!</h3>
              <p className="text-gray-600 mb-6">
                Grazie per esserti registrato. Ti contatteremo presto con tutte le informazioni sui prossimi eventi.
              </p>
              <button
                onClick={handleClose}
                className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-8 py-2.5 rounded-lg font-semibold transition-all"
              >
                Chiudi
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                <input
                  type="text"
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C] uppercase"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  placeholder="Es. Mario Rossi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Es. mario@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Es. +39 333 1234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sesso</label>
                <select
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                >
                  <option value="">Seleziona sesso</option>
                  <option value="uomo_etero">Uomo Etero</option>
                  <option value="donna_etero">Donna Etero</option>
                  <option value="uomo_gay">Uomo Gay</option>
                  <option value="donna_gay">Donna Gay</option>
                  <option value="non_dichiarato">Non dichiarato</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                <input
                  type="date"
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                  value={formData.birthdate}
                  onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Citta</label>
                <select
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#D91C5C] focus:ring-[#D91C5C]"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                >
                  {ITALIAN_PROVINCES.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D91C5C] hover:bg-[#C01850] text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  'Invia'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicRegistrationModal;
